/**
 * useCalendarEvents.js — Custom calendar events CRUD
 *
 * Manages non-job calendar events (meetings, deadlines, personal events).
 * Schema: id, title, date, time, notes, color, created_at, updated_at, user_id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { snakeToCamel, camelToSnake } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

const QUERY_KEY = ['calendar_events'];

function mapFromDb(row) { return snakeToCamel(row); }

export function useCalendarEvents() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      try {
        const data = await api.get('/calendar-events?order=date&dir=asc');
        return data.map(mapFromDb);
      } catch {
        // Table may not exist yet — return empty
        return [];
      }
    },
  });
}

export function useCreateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (event) => {
      const data = await api.post('/calendar-events', camelToSnake(event));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      logAction({
        action: 'Create Calendar Event',
        description: `Created calendar event ${data.title} on ${data.date}`,
        details: { title: data.title, date: data.date, time: data.time },
      });
    },
  });
}

export function useUpdateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }) => {
      const data = await api.put(`/calendar-events/${id}`, camelToSnake(fields));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      logAction({
        action: 'Update Calendar Event',
        description: `Updated calendar event ${data.title}`,
        details: { id: data.id, title: data.title, date: data.date, time: data.time },
      });
    },
  });
}

export function useDeleteCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.del(`/calendar-events/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      logAction({ action: 'Delete Calendar Event', description: 'Deleted a calendar event' });
    },
  });
}
